/**
 * hk_os · useVoiceRegistry
 * ────────────────────────────────────────────────────────────────────
 * React hook + provider that owns the Operational Assistant voice
 * manifest for a given locale. Consumers reference **snippet keys**
 * (e.g. VOICE_NEXT_TASK) and never see raw Bengali text — that stays
 * inside the OA backend / cache.
 *
 * Responsibilities:
 *   - Load `GET /api/oa/v1/voice/manifest?locale=<L>` (cache-validated).
 *   - Persist metadata + manifest JSON in localStorage for offline start.
 *   - Expose `getText(key)` / `getSnippet(key)` / `play(key)` / `stop()`.
 *   - Report `status` = loading | ready | offline | error.
 *
 * NO business logic. NO Bengali strings. NO hardcoded audio URLs.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  cacheAudio, matchAudio, openCache, readManifest, readManifestMeta,
  saveManifest, countCached,
} from "./voiceCache";

const API = process.env.REACT_APP_BACKEND_URL;

const VoiceRegistryContext = createContext(null);

/** Optional preloader: warms the browser cache with the manifest audio URLs. */
async function warmup(manifest) {
  if (!manifest?.snippets?.length) return { warmed: 0, total: 0 };
  let warmed = 0;
  await Promise.all(
    manifest.snippets.map(async (sn) => {
      if (!sn.audio_url) return;
      const ok = await cacheAudio(sn.audio_url, API);
      if (ok) warmed += 1;
    })
  );
  return { warmed, total: manifest.snippets.length };
}

export function VoiceRegistryProvider({
  children,
  locale = "bn-IN",
  autoWarmup = true,
  authToken,
}) {
  const [manifest, setManifest] = useState(() => readManifest());
  const [meta, setMeta] = useState(() => readManifestMeta());
  const [status, setStatus] = useState("idle");   // idle | loading | ready | offline | error
  const [warmedCount, setWarmedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const currentAudio = useRef(null);

  const load = useCallback(async () => {
    setStatus("loading");
    try {
      const headers = { "Content-Type": "application/json" };
      if (authToken) headers.Authorization = `Bearer ${authToken}`;
      const url = `${API}/api/oa/v1/voice/manifest?locale=${encodeURIComponent(locale)}`;
      const cur = readManifestMeta();
      const withCache = cur?.checksum
        ? `${url}&client_checksum=${cur.checksum}`
        : url;
      const r = await fetch(withCache, { headers, credentials: "include" });
      if (!r.ok) throw new Error(`manifest fetch ${r.status}`);
      const data = await r.json();
      if (data.cached) {
        // Server confirmed our cached manifest is current; keep old data.
        setStatus("ready");
        const cached = await countCached();
        setWarmedCount(cached);
        setTotalCount((readManifest()?.snippets || []).length);
        return;
      }
      saveManifest(data);
      setManifest(data);
      setMeta(readManifestMeta());
      setTotalCount(data.snippets.length);
      setStatus("ready");
      if (autoWarmup) {
        const { warmed, total } = await warmup(data);
        setWarmedCount(warmed);
        setTotalCount(total);
      }
    } catch (err) {
      // Offline / network — fall back to persisted manifest if any.
      const cached = readManifest();
      if (cached) {
        setStatus("offline");
        setManifest(cached);
        setMeta(readManifestMeta());
        setTotalCount(cached.snippets.length);
        setWarmedCount(await countCached());
      } else {
        setStatus("error");
      }
    }
  }, [locale, authToken, autoWarmup]);

  useEffect(() => { load(); }, [load]);

  const byKey = useMemo(() => {
    const map = new Map();
    (manifest?.snippets || []).forEach((sn) => map.set(sn.key, sn));
    return map;
  }, [manifest]);

  const getSnippet = useCallback((key) => byKey.get(key) || null, [byKey]);
  const getText = useCallback((key) => byKey.get(key)?.text || null, [byKey]);

  const stop = useCallback(() => {
    if (currentAudio.current) {
      try { currentAudio.current.pause(); } catch { /* noop */ }
      currentAudio.current = null;
    }
  }, []);

  /**
   * Play a snippet by key. Returns a Promise that resolves when the
   * audio finishes (or immediately when unavailable).
   */
  const play = useCallback(async (key, { onProgress } = {}) => {
    const sn = byKey.get(key);
    if (!sn?.audio_url) return { played: false, reason: "no_audio" };
    stop();
    const cached = await matchAudio(sn.audio_url, API);
    let src;
    if (cached) {
      const blob = await cached.blob();
      src = URL.createObjectURL(blob);
    } else {
      src = sn.audio_url.startsWith("http") ? sn.audio_url : `${API}${sn.audio_url}`;
    }
    const audio = new Audio(src);
    currentAudio.current = audio;
    return new Promise((resolve) => {
      audio.addEventListener("timeupdate", () => {
        if (audio.duration > 0) {
          onProgress?.(audio.currentTime / audio.duration);
        }
      });
      audio.addEventListener("ended", () => {
        onProgress?.(1);
        currentAudio.current = null;
        if (cached) URL.revokeObjectURL(src);
        resolve({ played: true, cached: !!cached });
      });
      audio.addEventListener("error", () => {
        currentAudio.current = null;
        resolve({ played: false, reason: "playback_error" });
      });
      audio.play().catch(() => {
        // Autoplay blocked → resolve; consumer can retry on user gesture.
        resolve({ played: false, reason: "autoplay_blocked" });
      });
    });
  }, [byKey, stop]);

  const refreshWarmup = useCallback(async () => {
    if (!manifest) return;
    const { warmed, total } = await warmup(manifest);
    setWarmedCount(warmed);
    setTotalCount(total);
  }, [manifest]);

  const value = useMemo(() => ({
    locale, manifest, meta, status,
    warmedCount, totalCount,
    isReady: status === "ready" || status === "offline",
    isOffline: status === "offline",
    getSnippet, getText, play, stop, reload: load, refreshWarmup,
  }), [locale, manifest, meta, status, warmedCount, totalCount, getSnippet, getText, play, stop, load, refreshWarmup]);

  return (
    <VoiceRegistryContext.Provider value={value}>
      {children}
    </VoiceRegistryContext.Provider>
  );
}

export function useVoiceRegistry() {
  const ctx = useContext(VoiceRegistryContext);
  if (!ctx) {
    // Provide a graceful degenerate impl so components can be dropped
    // outside the provider in Storybook / preview modes.
    return {
      locale: "bn-IN", manifest: null, meta: null, status: "idle",
      warmedCount: 0, totalCount: 0, isReady: false, isOffline: false,
      getSnippet: () => null, getText: () => null,
      play: async () => ({ played: false, reason: "no_provider" }),
      stop: () => {},
      reload: async () => {},
      refreshWarmup: async () => {},
    };
  }
  return ctx;
}

/** Convenience: pull a single snippet by key (returns null before load). */
export function useVoiceSnippet(key) {
  const { getSnippet } = useVoiceRegistry();
  return getSnippet(key);
}
